import { InfoOutlined } from "@mui/icons-material";
import { Box, Tooltip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Paragraph } from "components/Paragraph";
import { ProductExternalId } from "components/ProductExternalId";
import { Column, ResponsiveTable } from "components/ResponsiveTable";
import { Spinner } from "components/Spinner";
import { Title } from "components/Title";
import { productsPath } from "constants/routes";
import { useAuthentication } from "hooks/useAuthentication";
import { useLayout } from "hooks/useLayout";
import { OrderItem, Order as OrderModel } from "types/models";
import { API } from "utils/api";
import { samePrice } from "utils/models/order";
import { getDiscounts } from "utils/models/product";
import { isBluecarRole as isBluecarRoleUtils } from "utils/models/user";
import { toFormattedPrice } from "utils/numbers";

export const Order = () => {
  const { PublicLayout } = useLayout({ title: "Detalle de pedido" });
  const { orderId } = useParams();
  const navigate = useNavigate();

  const { isLoading: isLoadingUser, user } = useAuthentication();

  const isBluecarRole = useMemo(
    () => !!user && isBluecarRoleUtils(user),
    [user]
  );

  const { data: order, isLoading: isLoadingOrder } = useQuery<OrderModel>({
    queryKey: ["orders", orderId],
    queryFn: () => API.get(`orders/${orderId}`).then(({ data }) => data),
    enabled: !!orderId,
    staleTime: Infinity,
  });

  if (
    !orderId ||
    (!user && !isLoadingUser) ||
    (!order && !isLoadingOrder) ||
    (order && !isBluecarRole && order.company.id !== user?.company?.id)
  ) {
    navigate(productsPath);
  }

  const items = useMemo(() => order?.order_items ?? [], [order?.order_items]);

  const { subtotalPrice, totalPrice } = useMemo(() => {
    let subtotal = 0;
    let total = 0;

    items?.forEach((orderItem) => {
      const itemSubtotal =
        orderItem.amount * orderItem.product_itris_final_unit_price;
      subtotal += itemSubtotal;
      total += itemSubtotal * orderItem.product_tax_multiplier;
    });

    return { subtotalPrice: subtotal, totalPrice: total };
  }, [items]);

  const columns: Column<OrderItem>[] = useMemo(
    () => [
      {
        id: "product_id",
        label: "Producto",
        renderer: (orderItem) => (
          <>
            <Paragraph ellipsis uppercase>
              {orderItem.brand_name}
              {orderItem.brand_name && orderItem.group_name ? " - " : ""}
              {orderItem.group_name}
              {orderItem.group_name && orderItem.line_name ? " - " : ""}
              {orderItem.line_name}
            </Paragraph>
            <Title
              onClick={() =>
                navigate(`${productsPath}/${orderItem.product_id}`)
              }
              font="sm"
              ellipsis
              tooltip
              uppercase
            >
              {orderItem.product_name}
            </Title>
            <ProductExternalId externalId={orderItem.product_external_id} />
          </>
        ),
      },
      {
        id: "amount",
        label: "Cantidad",
        align: "center",
      },
      {
        id: "product_price",
        label: "Precio unitario",
        align: "center",
        renderer: (orderItem) =>
          samePrice(orderItem) ? (
            <Paragraph font="l">
              ${toFormattedPrice({ number: orderItem.product_price ?? 0 })}
            </Paragraph>
          ) : (
            <Paragraph font="l">
              $
              {toFormattedPrice({
                number: orderItem.product_itris_final_unit_price,
              })}
            </Paragraph>
          ),
      },
      {
        id: "product_price",
        label: "Precio unitario con descuento",
        align: "center",
        renderer: (orderItem) => {
          if (!samePrice(orderItem)) return "-";

          return (
            <Paragraph font="l">
              $
              {toFormattedPrice({ number: orderItem.product_final_unit_price })}
            </Paragraph>
          );
        },
      },
      {
        id: "promotion_discount",
        label: "Descuentos",
        align: "center",
        renderer: (orderItem) => {
          if (!samePrice(orderItem)) return "-";

          const discounts = [];

          const allDiscounts = getDiscounts({
            amount: orderItem.amount,
            companyBaseDiscount: order?.company_base_discount,
            companyDiscount: orderItem.company_discount,
            promotionDiscount: orderItem.promotion_discount,
            promotionMinimumAmount: orderItem.promotion_minimum_amount,
          });

          if (allDiscounts.companyBaseDiscount) {
            discounts.push(
              <Paragraph
                tooltip
                tooltipText={`Descuento base ${order?.company.name}`}
                font="l"
              >
                {allDiscounts.companyBaseDiscount}%
              </Paragraph>
            );
          }

          if (
            allDiscounts.companyBaseDiscount &&
            allDiscounts.companyDiscount
          ) {
            discounts.push(" + ");
          }

          if (allDiscounts.companyDiscount) {
            discounts.push(
              <Paragraph
                tooltip
                tooltipText="Descuento en marca/producto"
                font="l"
              >
                {allDiscounts.companyDiscount}%
              </Paragraph>
            );
          }

          if (allDiscounts.promotionDiscount) {
            discounts.push(
              <Paragraph
                tooltip
                tooltipText={`Promoción a partir de ${orderItem.promotion_minimum_amount} unidades`}
                font="l"
              >
                {allDiscounts.promotionDiscount}%
              </Paragraph>
            );
          }

          return discounts.length ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap="5px"
            >
              <Tooltip
                title="Posa el mouse sobre el descuento para ver más información"
                arrow
              >
                <InfoOutlined
                  fontSize="small"
                  sx={{ transform: "scale(85%)" }}
                />
              </Tooltip>
              {discounts}
            </Box>
          ) : (
            "-"
          );
        },
      },
      {
        id: "product_price",
        label: "Subotal con descuento",
        align: "center",
        renderer: (orderItem) => {
          if (!samePrice(orderItem)) {
            return (
              <Box>
                <Title font="sm" style={{ marginX: { xs: 1, sm: 0 } }}>
                  $
                  {toFormattedPrice({
                    number:
                      orderItem.product_itris_final_unit_price *
                      orderItem.amount,
                  })}
                </Title>
              </Box>
            );
          }
          const hasDiscount =
            orderItem.product_final_unit_price !== orderItem.product_price;
          return (
            <Box>
              {hasDiscount && (
                <Paragraph font="sm" style={{ textDecoration: "line-through" }}>
                  $
                  {toFormattedPrice({
                    number: orderItem.product_price! * orderItem.amount,
                  })}
                </Paragraph>
              )}
              <Title font="sm" style={{ marginX: { xs: 1, sm: 0 } }}>
                $
                {toFormattedPrice({
                  number: orderItem.product_final_unit_price * orderItem.amount,
                })}
              </Title>
            </Box>
          );
        },
      },
      {
        id: "product_itris_final_unit_price",
        label: "Total con descuento",
        align: "center",
        renderer: (orderItem) => (
          <Box>
            <Paragraph font="sm">(IVA {orderItem.product_human_tax})</Paragraph>
            <Title font="sm" style={{ marginX: { xs: 1, sm: 0 } }}>
              $
              {toFormattedPrice({
                number:
                  orderItem.product_itris_final_unit_price *
                  orderItem.amount *
                  orderItem.product_tax_multiplier,
              })}
            </Title>
          </Box>
        ),
      },
    ],
    [navigate, order?.company, order?.company_base_discount]
  );

  if (isLoadingUser || isLoadingOrder || !items)
    return (
      <PublicLayout>
        <Box paddingY={3} paddingX={{ xs: 3, md: 7 }} minHeight={800}>
          <Spinner />
        </Box>
      </PublicLayout>
    );
  if (!user) navigate(productsPath);

  return (
    <PublicLayout>
      <Box paddingY={3} paddingX={{ xs: 3, md: 7 }} minHeight={800}>
        <Title
          style={{
            marginTop: 2,
            marginBottom: 2,
          }}
        >
          Pedido ID {orderId} - {order?.company.name}
        </Title>
        <ResponsiveTable data={items} columns={columns} />
        <Title font="sm" style={{ marginTop: 2 }}>
          Subtotal: ${toFormattedPrice({ number: subtotalPrice })}
        </Title>
        <Title font="md">
          Total: ${toFormattedPrice({ number: totalPrice })}
        </Title>
        <Title font="sm" style={{ marginTop: 1 }}>
          Envío: {order?.delivery ? "Sí" : "Retiro en sucursal"}
        </Title>
        <Title font="sm">
          {order?.delivery ? "Dirección" : "Sucursal"}: {order?.address}
        </Title>
      </Box>
    </PublicLayout>
  );
};
